// @ts-check
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '~/design-system';

/**
 * @param {object} props
 * @param {string | import('history').Location} [props.to] The internal link target to go to.
 * @param {string} [props.href] An external link to go to.
 * @param {((args0: any)=>void)} [props.onClick] A click handler. I this case the link is rendered as a <button> semantically.
 * @param {string} [props.variant] The link's variant, defined in [linkVariants](./linkVariants.js).
 * @param {string} [props.className] The link styles.
 * @param {boolean} [props.replace] If set, the link replaces the current history entry.
 * @param {React.ReactNode} [props.children]
 * @param {React.ElementType} [props.as] An alternative tag to render the link as.
 * @param {string} [props.target] The link target.
 * @param {boolean} [props.noopener] Doesn't transfer the opener to the target.
 * @param {boolean} [props.noreferrer] Doesn't transfer the referrer to the target.
 * @param {React.ElementType} [props.fallback] An optional fallback if no link was provided.
 * @returns {React.ReactElement}
 */
export default function Link({
  to,
  href,
  onClick,
  variant = 'default',
  fallback = 'span',
  replace,
  ...rest
}) {
  if (to) {
    return (
      <RouterLink
        // @ts-ignore
        to={to}
        replace={replace}
        onClick={onClick}
        sx={{
          ':focus': { textDecoration: 'none' }, // FIXME Bootstrap style spill.
          variant: `link.${variant}`,
          cursor: 'pointer',
        }}
        {...rest}
      />
    );
  }
  if (href) {
    return (
      <Box
        as="a"
        // @ts-ignore
        href={href}
        onClick={onClick}
        sx={{
          ':focus': { textDecoration: 'none' }, // FIXME Bootstrap style spill.
          variant: `link.${variant}`,
          cursor: 'pointer',
        }}
        {...rest}
      />
    );
  }
  if (onClick) {
    return (
      <Box
        as="button"
        onClick={onClick}
        sx={{ variant: `link.${variant}`, cursor: 'pointer' }}
        {...rest}
      />
    );
  }
  return (
    <Box
      as={fallback}
      sx={{ variant: `link.${variant}`, cursor: 'pointer' }}
      {...rest}
    />
  );
}
